import {
  Box,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState, useCallback } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";
import { CardField } from "src/utils/card/cardData";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";

interface FallRiskHarizmiFormData {
  FRH_ID: string;
  PROCESS_TIME: Date | null;
  NURSE_ID: string;
  CTIME: Date;
  CUSER: string;
  AGE: string;
  AGE_XVALUE: string;
  FALL_HISTORY: string;
  FALL_HISTORY_XVALUE: string;
  DISEASES: string;
  DISEASES_XVALUE: string;
  SIGHT_CONDITION: string;
  SIGHT_CONDITION_XVALUE: string;
  MEDICINE: string;
  MEDICINE_XVALUE: string;
  EQUIPMENT: string;
  EQUIPMENT_XVALUE: string;
  WALKER_SUPPORT: string;
  WALKER_SUPPORT_XVALUE: string;
  SEDASYON_ANESTEZI: string;
  SEDASYON_ANESTEZI_XVALUE: string;
  MENTAL_STATE: string;
  MENTAL_STATE_XVALUE: string;
  STABILITY: string;
  STABILITY_XVALUE: string;
  BED: string;
  BED_XVALUE: string;
  FAMILY_INFORMATION: string;
  FAMILY_INFORMATION_XVALUE: string;
  JUSTIFICATION_EVALUATION: string;
  JUSTIFICATION_EVALUATION_XVALUE: string;
  NURSE_NOTE: string;
  GENARAL_NOTE: string;
  CALC_VALUE: number;
}

interface FallRiskHarizmiProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof FallRiskHarizmiFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function FallRiskHarizmiCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: FallRiskHarizmiProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "FALL_RISK_HARIZMI",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof FallRiskHarizmiFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as FallRiskHarizmiFormData;

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(false);
  }, [patientData?.data?.id]);

  useEffect(() => {
    if (!loading && mcpCacheLoaded && !isInitialized) {
      if (!isEditMode) {
        resetForm();
      }

      const patientInfo = patientData?.data;

      if (patientInfo && patientInfo.birthdate) {
        const currentAge = typedFormData.AGE;

        if (!isEditMode || (isEditMode && !currentAge)) {
          const birthDate = new Date(patientInfo.birthdate);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }

          const ageDetails = getMCPParameterDetails(70);
          const realAge = Math.max(0, age);

          const ageDetail = ageDetails.find((d) => {
            const normalizedName = d.name.replace(/\s/g, "");
            if (realAge >= 0 && realAge <= 3 && normalizedName.includes("0-3"))
              return true;
            if (realAge >= 4 && realAge <= 7 && normalizedName.includes("4-7"))
              return true;
            if (realAge >= 8 && realAge <= 11 && normalizedName.includes("8-11"))
              return true;
            if (realAge >= 12 && realAge <= 18 && normalizedName.includes("12-18"))
              return true;
            return false;
          });

          if (ageDetail) {
            handleMCPChange("AGE", ageDetail);
          }
        }
      }
      setIsInitialized(true);
    }
  }, [
    isEditMode,
    resetForm,
    patientData,
    mcpCacheLoaded,
    getMCPParameterDetails,
    handleMCPChange,
    isInitialized,
    loading,
    typedFormData.AGE,
  ]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "RESULT") {
        parentHandleChange(key as keyof FallRiskHarizmiFormData, value);
      }
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;

    const rowData: any = {
      AGE_XVALUE: getScoreValue("AGE"),
      FALL_HISTORY: typedFormData.FALL_HISTORY,
      DISEASES_XVALUE: getScoreValue("DISEASES"),
      SIGHT_CONDITION_XVALUE: getScoreValue("SIGHT_CONDITION"),
      MEDICINE: typedFormData.MEDICINE,
      EQUIPMENT: typedFormData.EQUIPMENT,
      WALKER_SUPPORT: typedFormData.WALKER_SUPPORT,
      SEDASYON_ANESTEZI_XVALUE: getScoreValue("SEDASYON_ANESTEZI"),
      MENTAL_STATE: typedFormData.MENTAL_STATE,
      STABILITY: typedFormData.STABILITY,
      BED: typedFormData.BED,
      FAMILY_INFORMATION: typedFormData.FAMILY_INFORMATION,
    };

    const fallRiskField = cardData.fields.find(
      (f: any) => f.field === "fall-risk-harizmi-score"
    ) as CardField | undefined;
    if (fallRiskField?.calculate) {
      const calculatedScore = fallRiskField.calculate(rowData);
      setScore(calculatedScore);
      parentHandleChange("CALC_VALUE", calculatedScore);
    }
  }, [
    cardData,
    getScoreValue,
    parentHandleChange,
    typedFormData,
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate]);


  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number,
    readOnly: boolean = false,
    cantChange: boolean = false
  ) => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6}>
        {readOnly ? (
          <ByzMuiTextField
            sx={{ width: "100%" }}
            color="info"
            variant="outlined"
            value={selectedMCPs[fieldName]?.name || ""}
            cantChange
          />
        ) : (
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={mcpId}
            multiple={false}
            handleChange={(val) => {
              if (!Array.isArray(val)) {
                handleMCPChange(fieldName, val);
              }
            }}
            value={selectedMCPs[fieldName]}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(mcpId)}
            cacheLoaded={mcpCacheLoaded}
          />
        )}
      </Grid>
    </Grid>
  );

  const renderCheckboxField = (
    fieldName: string,
    labelKey: string
  ) => {
    const isChecked = typedFormData[fieldName as keyof FallRiskHarizmiFormData] === "1" ||
      typedFormData[fieldName as keyof FallRiskHarizmiFormData] === 1;

    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid
          xs={12}
          sm={6}
          md={6}
          sx={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <ByzMuiTypography sx={{ marginLeft: "30px" }}>
            {t(labelKey)}
          </ByzMuiTypography>
        </Grid>
        <Grid xs={12} sm={6} md={6}>
          <ByzMuiCheckBox
            checked={isChecked}
            onChange={(e) => {
              handleChange(fieldName, e.target.checked ? "1" : "0");
            }}
            color="primary"
            disabled={!extraAccess}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.FRH_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("fall-risk-harizmi.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ width: "100%" }}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                  label={t("datetime")}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            {renderMCPField("JUSTIFICATION_EVALUATION", "fall-risk-harizmi.justification-evaluation", 82, !extraAccess)}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("fall-risk-harizmi.risk-factors")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField("AGE", "fall-risk-harizmi.age", 70, true)}
            {renderCheckboxField("FALL_HISTORY", "fall-risk-harizmi.fall-history")}
            {renderMCPField("DISEASES", "fall-risk-harizmi.diseases", 71, !extraAccess)}
            {renderMCPField("SIGHT_CONDITION", "fall-risk-harizmi.sight-condition", 73, !extraAccess)}
            {renderCheckboxField("MEDICINE", "fall-risk-harizmi.medicine")}
            {renderCheckboxField("EQUIPMENT", "fall-risk-harizmi.equipment")}
            {renderCheckboxField("WALKER_SUPPORT", "fall-risk-harizmi.walker-support")}
            {renderMCPField("SEDASYON_ANESTEZI", "fall-risk-harizmi.sedasyon-anestezi", 77, !extraAccess)}
            {renderCheckboxField("MENTAL_STATE", "fall-risk-harizmi.mental-state")}
            {renderCheckboxField("STABILITY", "fall-risk-harizmi.stability")}
            {renderCheckboxField("BED", "fall-risk-harizmi.bed")}
            {renderCheckboxField("FAMILY_INFORMATION", "fall-risk-harizmi.family-information")}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("fall-risk-harizmi.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("score")}
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("note")}
                  value={typedFormData.NURSE_NOTE}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default FallRiskHarizmiCreate;
